// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 20-agent-plugins.md#marketplace-repo-bootstrap
// @greenlight-spec: decisions/0040-multiple-plugin-marketplace-repositories.md

import * as React from 'react';

import { Disclosure } from '@/components/greenlight/Disclosure';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { PluginMarketplaceStatus, PluginMarketplaceVisibility } from '@greenlight/shared';

import { MarketplaceRepositoryTable } from './MarketplaceRepositoryTable';
import { UnregisterMarketplaceDialog } from './UnregisterMarketplaceDialog';
import {
  type BootstrapMarketplace,
  type UnregisterMarketplace,
  useMarketplaceEditor,
} from './useMarketplaceEditor';

export interface MarketplaceEditorProps {
  repositories: PluginMarketplaceStatus[];
  githubAppReady: boolean;
  onBootstrap: BootstrapMarketplace;
  /** Omitted by the install wizard step — unregistering is a Settings-only capability. */
  onUnregister?: UnregisterMarketplace;
  /** SCM org handle shown as a fixed prefix on the repository name field. */
  orgHandle?: string | null;
  /**
   * Where this editor is mounted — the stale-installation recovery message
   * points at "Start over" on the wizard's GitHub App step (mid-wizard only,
   * per docs/07 §Re-install semantics) or at Settings' "Re-create App" action
   * (the only recovery once install is complete), never both.
   */
  recoveryContext: 'wizard' | 'settings';
  /**
   * Set to `outline` where creating a repository is no longer the surface's
   * primary action — the install wizard demotes it once a repository exists and
   * Continue takes over, so only one filled button is on screen.
   */
  createVariant?: 'default' | 'outline';
  /** Scroll to the create form on mount and focus its name field (a direct link to create). */
  focusCreate?: boolean;
  className?: string;
}

const VISIBILITY_OPTIONS: ReadonlyArray<{
  value: PluginMarketplaceVisibility;
  label: string;
  recommended: boolean;
  summary: string;
}> = [
  {
    value: 'private',
    label: 'Private',
    recommended: true,
    summary:
      'Claude, Codex and Cursor sync it, and Copilot reads it with each person’s GitHub sign-in.',
  },
  {
    value: 'public',
    label: 'Public',
    recommended: false,
    summary: 'Fallback when the Greenlight agent plugin can’t be private.',
  },
];

export function MarketplaceEditor({
  repositories,
  githubAppReady,
  onBootstrap,
  onUnregister,
  orgHandle = null,
  recoveryContext,
  createVariant = 'default',
  focusCreate = false,
  className,
}: MarketplaceEditorProps): React.JSX.Element {
  const createFormRef = React.useRef<HTMLDivElement>(null);
  const repoNameRef = React.useRef<HTMLInputElement>(null);
  const {
    visibility,
    setVisibility,
    repoName,
    setRepoName,
    repoNameInvalid,
    repoNameTaken,
    canCreate,
    pendingKey,
    error,
    errorCode,
    create,
    reconcile,
    unregisterTarget,
    requestUnregister,
    cancelUnregister,
    unregisterPending,
    unregisterError,
    confirmUnregister,
  } = useMarketplaceEditor(onBootstrap, onUnregister, repositories);

  const createEnabled = githubAppReady && canCreate;
  const formBusy = pendingKey !== null || !githubAppReady;
  const orgName = orgHandle ?? 'your GitHub organization';

  React.useEffect(() => {
    if (!focusCreate) return;
    createFormRef.current?.scrollIntoView?.({ block: 'center' });
    repoNameRef.current?.focus({ preventScroll: true });
  }, [focusCreate]);

  const createForm = (
    <div ref={createFormRef} className="flex flex-col gap-4" data-testid="marketplace-create-form">
      <fieldset className="space-y-2" disabled={formBusy}>
        <legend className="text-sm font-medium leading-none">Plugin repo visibility</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {VISIBILITY_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={cn(
                'flex cursor-pointer gap-3 rounded-md border border-input bg-background p-3 text-sm',
                'has-[:checked]:border-foreground has-[:focus-visible]:ring-1 has-[:focus-visible]:ring-ring',
                formBusy && 'cursor-not-allowed opacity-50',
              )}
            >
              <input
                type="radio"
                name="marketplace-visibility"
                value={option.value}
                checked={visibility === option.value}
                onChange={() => setVisibility(option.value)}
                aria-label={option.label}
                className="mt-0.5 size-4 shrink-0 accent-foreground"
                data-testid={`marketplace-visibility-${option.value}`}
              />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="font-medium">
                  {option.label}
                  {option.recommended && (
                    <span className="font-normal text-muted-foreground"> · Recommended</span>
                  )}
                </span>
                <span className="text-xs text-muted-foreground">{option.summary}</span>
              </span>
            </label>
          ))}
        </div>
        {recoveryContext === 'wizard' && (
          <p className="text-xs text-muted-foreground" data-testid="marketplace-change-later">
            You can change this later in Settings → Source control.
          </p>
        )}
      </fieldset>

      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
        <div className="space-y-2">
          <Label htmlFor="marketplace-repo-name">Agent plugin repository name</Label>
          <div
            className={cn(
              'flex h-9 w-full overflow-hidden rounded-md border border-input bg-transparent shadow-sm',
              'focus-within:ring-1 focus-within:ring-ring',
              formBusy && 'opacity-50',
            )}
            data-testid="marketplace-repo-field"
          >
            <span
              className="flex shrink-0 items-center border-r border-input bg-muted/40 px-3 font-mono text-sm text-muted-foreground"
              data-testid={
                orgHandle
                  ? 'marketplace-repo-org-prefix'
                  : 'marketplace-repo-org-prefix-placeholder'
              }
            >
              {orgHandle ?? 'org'}/
            </span>
            <Input
              ref={repoNameRef}
              id="marketplace-repo-name"
              value={repoName}
              maxLength={100}
              onChange={(event) => setRepoName(event.target.value)}
              disabled={formBusy}
              aria-invalid={repoNameInvalid}
              aria-describedby={
                !githubAppReady
                  ? 'marketplace-create-waiting'
                  : repoNameInvalid
                    ? 'marketplace-repo-name-error'
                    : repoNameTaken
                      ? 'marketplace-repo-name-taken'
                      : undefined
              }
              className="h-full border-0 shadow-none focus-visible:ring-0"
              data-testid="marketplace-repo-input"
            />
          </div>
          {repoNameInvalid && (
            <p id="marketplace-repo-name-error" className="text-xs text-destructive">
              Use only letters, numbers, &ldquo;.&rdquo;, &ldquo;-&rdquo;, and &ldquo;_&rdquo;.
            </p>
          )}
          {!repoNameInvalid && repoNameTaken && (
            <p
              id="marketplace-repo-name-taken"
              className="text-xs text-muted-foreground"
              data-testid="marketplace-repo-name-taken"
            >
              Already created. Use <span className="font-medium">Check repo</span> in the list to
              re-run it, or enter a different name.
            </p>
          )}
        </div>
        <Button
          type="button"
          variant={createVariant}
          onClick={() => void create()}
          disabled={!createEnabled}
          className="h-11 sm:mt-7 sm:h-9"
          data-testid="marketplace-create"
        >
          {pendingKey === 'create'
            ? 'Creating…'
            : githubAppReady
              ? 'Create repository'
              : 'Waiting for GitHub…'}
        </Button>
      </div>
      {!githubAppReady && (
        <p
          id="marketplace-create-waiting"
          className="text-xs text-muted-foreground"
          data-testid="marketplace-create-waiting"
        >
          Create stays disabled until the GitHub App installation is connected.
        </p>
      )}

      <Disclosure label="More about repo visibility" testId="marketplace-visibility-detail">
        <div className="flex flex-col gap-3 text-sm text-muted-foreground">
          <section className="space-y-1" data-testid="marketplace-visibility-private-detail">
            <h4 className="font-medium text-foreground">Private repo</h4>
            <p>
              Serves every organization rollout. Claude, Codex and Cursor each sync it through their
              own GitHub connection and serve members a copy. Copilot fetches it with each
              person&rsquo;s GitHub sign-in, so members of{' '}
              <span className="font-mono">{orgName}</span> need nothing more.
            </p>
          </section>
          <section className="space-y-1" data-testid="marketplace-visibility-public-detail">
            <h4 className="font-medium text-foreground">Public repo</h4>
            <p>
              Add one when some Copilot users are not members of{' '}
              <span className="font-mono">{orgName}</span> on GitHub, for Claude Code sessions
              without a claude.ai sign-in, or for people installing the plugin themselves without
              Git access to the private repo. Both repos hold the same plugin.
            </p>
          </section>
        </div>
      </Disclosure>

      {error && errorCode === 'plugin_marketplace.scm_installation_invalid' ? (
        <Alert variant="destructive" data-testid="marketplace-installation-invalid">
          <AlertDescription>
            {recoveryContext === 'wizard' ? (
              <>
                The connected GitHub App installation is stale and Greenlight can no longer act
                through it. Go back to the <strong>GitHub App</strong> step above, choose{' '}
                <strong>Start over</strong>, then reinstall the App into your organization.
              </>
            ) : (
              <>
                The connected GitHub App installation is stale and Greenlight can no longer act
                through it. Go to <strong>Source control</strong> above and choose{' '}
                <strong>Re-create App</strong> to reconnect it.
              </>
            )}
          </AlertDescription>
        </Alert>
      ) : (
        error && (
          <Alert variant="destructive" data-testid="marketplace-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )
      )}
    </div>
  );

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {!githubAppReady && (
        <div
          className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground"
          data-testid="marketplace-blocked"
        >
          Waiting for the GitHub App connection before repositories can be created.
        </div>
      )}

      {createForm}
      {repositories.length > 0 && (
        <MarketplaceRepositoryTable
          repositories={repositories}
          pendingKey={pendingKey}
          onReconcile={reconcile}
          onUnregister={onUnregister ? requestUnregister : undefined}
        />
      )}

      <UnregisterMarketplaceDialog
        repository={unregisterTarget}
        pending={unregisterPending}
        error={unregisterError}
        onConfirm={confirmUnregister}
        onCancel={cancelUnregister}
      />
    </div>
  );
}
